import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Star, PartyPopper } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LaisserAvis() {
  const [form, setForm] = useState({ nom: "", evenement: "", texte: "", note: 5 });
  const [hover, setHover] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/avis/public`, { ...form, note: Number(form.note) });
      setSent(true);
    } catch {
      toast.error("Envoi impossible", { description: "Vérifiez les champs (texte de 10 caractères minimum) et réessayez." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14]">
      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="avis-back-link"
            className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <div className="mx-auto max-w-xl px-4 py-16 sm:px-6" data-testid="laisser-avis-page">
        {sent ? (
          <div className="flex flex-col items-center pt-10 text-center" data-testid="avis-success">
            <PartyPopper size={40} className="text-cyan-400" />
            <h1 className="font-display mt-6 text-3xl font-extrabold text-slate-50">Merci beaucoup !</h1>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Votre avis a bien été envoyé. Il sera publié sur le site après validation par nos soins.
              Merci d'avoir dansé avec KLMProd !
            </p>
            <Link
              to="/"
              className="bg-brand-gradient mt-8 rounded-full px-6 py-3 text-xs font-bold text-[#04121f] transition-transform duration-300 hover:scale-[1.03]"
            >
              Retour à l'accueil
            </Link>
          </div>
        ) : (
          <>
            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-cyan-400">Votre avis compte</p>
            <h1 className="font-display mt-3 text-3xl font-extrabold text-slate-50 sm:text-4xl">
              Comment était <span className="text-gradient">votre soirée ?</span>
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Une minute suffit. Votre retour rassure les futurs mariés et nous aide à nous améliorer.
            </p>

            <div className="mt-10 space-y-5 rounded-3xl border border-cyan-500/15 bg-[#0E1524]/70 p-6 sm:p-8">
              <div>
                <label className="mb-2 block text-xs font-semibold text-slate-300">Votre note</label>
                <div className="flex gap-1.5" data-testid="avis-note-selector">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, note: n }))}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      data-testid={`avis-note-${n}`}
                      className="transition-transform duration-200 hover:scale-125"
                      aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                    >
                      <Star
                        size={28}
                        className={n <= (hover || form.note) ? "fill-amber-300 text-amber-300" : "text-slate-700"}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold text-slate-300">Votre nom</label>
                <Input
                  value={form.nom}
                  onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                  placeholder="Ex. Sophie & Marc"
                  data-testid="avis-nom-input"
                  className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold text-slate-300">Votre événement (facultatif)</label>
                <Input
                  value={form.evenement}
                  onChange={(e) => setForm((f) => ({ ...f, evenement: e.target.value }))}
                  placeholder="Ex. Mariage — Valence, juin 2026"
                  data-testid="avis-evenement-input"
                  className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold text-slate-300">Votre avis</label>
                <textarea
                  value={form.texte}
                  onChange={(e) => setForm((f) => ({ ...f, texte: e.target.value }))}
                  placeholder="Racontez votre soirée en quelques mots…"
                  rows={4}
                  data-testid="avis-texte-input"
                  className="w-full rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
                />
              </div>

              <button
                onClick={submit}
                disabled={sending || form.nom.trim().length < 2 || form.texte.trim().length < 10}
                data-testid="avis-submit-button"
                className="bg-brand-gradient flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold text-[#04121f] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />}
                Envoyer mon avis
              </button>
              <p className="text-center text-[11px] text-slate-600">
                Votre avis sera publié après validation. Aucune donnée n'est partagée avec des tiers.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
